import type { FC } from 'react'

import { Button } from '@skytech/ui/Button'
import { useQuery } from '@tanstack/react-query'
import FileDelimitedIcon from 'mdi-react/FileDelimitedIcon'
import MicrosoftExcelIcon from 'mdi-react/MicrosoftExcelIcon'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useIsThreeStepIT } from '@/hooks/access'
import queries from '@/modules/admin/queries'

import { exportDataToExcel } from '../../helpers'
import type { ReportFilters, ReportSetupExportSettings } from '../../resources'
import adminResources from '../../resources'

export const ConsumptionOptions: FC<{
  title: string
  filters: ReportFilters
  reportSetupExportSettings?: ReportSetupExportSettings
}> = ({ filters, title }) => {
  const isThreeStepIT = useIsThreeStepIT()

  const { data: reports } = useQuery(queries.adminConsumptionReports(filters), () =>
    adminResources.getConsumptionReports(filters)
  )
  const translate = useTranslation()

  if (!reports) throw Error('We have encountered a problem while loading reports')

  const { fromDate, toDate } = filters
  if (!fromDate || !toDate) throw Error('Missing date range filters!')

  const reportsData = reports.map(report => ({
    code: report.code,
    name: report.name,
    employeeNo: report.employeeNo,
    depName: report.depName,
    depCode: report.depCode,
    benefitMobile: report.benefitMobile,
    benefitMobileCeiling: report.benefitMobileCeiling,
    deductionAmount: report.deductionAmount,
    taxReportAmount: report.taxReportAmount,
    subscriptionAmount: report.subscriptionAmount,
    trafficAmount: report.trafficAmount,
    oneTimeAmount: report.oneTimeAmount,
    totalAmount: report.totalAmount,
    emne: report.emne,
    orgNo: report.orgNo,
    company: report.company,
  }))

  const columnFormats = {
    deductionAmount: 'amount',
    taxReportAmount: 'amount',
    subscriptionAmount: 'amount',
    trafficAmount: 'amount',
    oneTimeAmount: 'amount',
    totalAmount: 'amount',
    benefitMobileCeiling: 'amount',
  }
  return (
    <div className="grid grid-cols-2">
      <Button
        Icon={<MicrosoftExcelIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
        className="mb-4 ml-0"
        onClick={() =>
          exportDataToExcel(reportsData, title, {
            translate,
            columnFormats,
            fromDate: filters.fromDate,
            toDate: filters.toDate,
          })
        }
      >
        <Translation id="Excel" />
      </Button>
      <Button
        Icon={<FileDelimitedIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
        className="mb-4 ml-0"
        onClick={() =>
          exportDataToExcel(reports, title, {
            extension: 'csv',
            translate,
            columnFormats,
            fromDate: filters.fromDate,
            toDate: filters.toDate,
          })
        }
      >
        <Translation id="CSV" />
      </Button>
    </div>
  )
}
